import _ from 'lodash';
import { isBlank } from 'underscore.string';

import {
  canadianProvincesMapToShortened,
  countryMapToShortened,
  USStatesMapToShortened,
} from 'shared/core/constants/geography';

const parseStateShortCode = (option1, option2) => {
  const splitByDash = _.split(option1 || option2, '-');
  return splitByDash[splitByDash.length - 1];
};

export const getCityToDisplay = (location) =>
  location?.rootType === 'locality' && location?.locality ? location?.locality : location?.city;

export const hasStreetNumber = (address) => {
  const re = /^\d+$/;
  return address ? re.test(_.split(address, ' ')[0]) : false;
};

export const convertMetersToMiles = (meters = 0.0) => meters * 0.000621371192;

export const buildLocationObjectFromGoogle = (data) => {
  const findByType = (components, type) => _.find(components, (comp) => _.includes(comp.types || [], type));

  return {
    streetNumber: _.get(findByType(data.address_components, 'street_number'), 'short_name'),
    streetName: _.get(findByType(data.address_components, 'route'), 'short_name'),
    ln1: [
      _.get(findByType(data.address_components, 'street_number'), 'short_name'),
      _.get(findByType(data.address_components, 'route'), 'short_name'),
    ].join(' '),
    ln2: _.get(findByType(data.address_components, 'subpremise'), 'short_name', ''),
    city:
      _.get(findByType(data.address_components, 'locality'), 'short_name') ||
      _.get(findByType(data.address_components, 'neighborhood'), 'short_name') ||
      _.get(findByType(data.address_components, 'administrative_area_level_3'), 'short_name') ||
      _.get(findByType(data.address_components, 'sublocality_level_1'), 'short_name'),
    county: _.get(findByType(data.address_components, 'administrative_area_level_2'), 'short_name'),
    state: _.get(findByType(data.address_components, 'administrative_area_level_1'), 'short_name'),
    zipcode: _.get(findByType(data.address_components, 'postal_code'), 'short_name'),
    country: _.get(findByType(data.address_components, 'country'), 'long_name'),
  };
};

export const formatAddressForLocationSearch = (location) => {
  const { ln1, ln2, state, stateLong, zipcode, country, countryLong, rootType } = location;
  const countryPretty =
    rootType === 'region' || rootType === 'country' ? countryLong : `${country === 'US' ? 'USA' : country}`;
  const statePretty = rootType === 'place' || rootType === 'region' ? stateLong : state;
  const cityPretty = getCityToDisplay(location);

  return _.reject(
    [
      _.reject([ln1, ln2], isBlank).join(' '),
      cityPretty,
      _.reject([statePretty, zipcode], isBlank).join(' '),
      countryPretty,
    ],
    isBlank
  ).join(', ');
};

// see https://bit.ly/3bg5aBi for details on the Mapbox Geocoding response object

/**
 * @param data {import('@mapbox/mapbox-sdk/services/geocoding').GeocodeFeature}
 *
 * @typedef {{
 * id: string,
 * lat: number,
 * lng: number,
 * ln1?: string,
 * city: string,
 * state: string,
 * address: string,
 * addressLong: import('@mapbox/mapbox-sdk/services/geocoding').GeocodeFeature['place_name']
 * zipcode: string;
 * }} LocationObject
 * @returns {LocationObject}
 *
 */
export const buildLocationObjectFromMapboxFeature = (data) => {
  const { id, center, place_name: addressLong } = data;
  const [lng, lat] = center;
  const locationDetails = _.reduce(
    [{ ...data, isRoot: true }, ...(data.context || [])],
    (accumulator, current) => {
      // eslint-disable-next-line camelcase
      const { address, id: currentId, text, short_code, properties } = current;
      const [type] = _.split(currentId, '.');

      if (current.isRoot) {
        accumulator.rootType = type;
      }

      if (type === 'country') {
        accumulator.country = countryMapToShortened[text];
        accumulator.countryLong = text;
      } else if (type === 'region') {
        accumulator.state =
          USStatesMapToShortened[text] ??
          canadianProvincesMapToShortened[text] ??
          parseStateShortCode(short_code, properties?.short_code);
        accumulator.stateLong = text;
      } else if (type === 'postcode') {
        accumulator.zipcode = text;
      } else if (type === 'district') {
        accumulator.county = text;
      } else if (type === 'locality') {
        accumulator.locality = text;
      } else if (type === 'place') {
        accumulator.city = text;
      } else if (type === 'neighborhood') {
        accumulator.neighborhood = text;
      } else if (type === 'address') {
        // TODO: see if there's a way to get ln2 (Ex: Apt 111) in Mapbox
        accumulator.ln1 = `${address ? `${address} ` : ''}${text}`;
      }

      return accumulator;
    },
    {}
  );

  return {
    id,
    ...locationDetails,
    lat,
    lng,
    address: formatAddressForLocationSearch(locationDetails),
    addressLong,
  };
};

export const formatAddress = (location) => {
  const { ln1, ln2, city, locality, state, zipcode, country } = location;

  const preferredAddressAttributesExist = !_.isEmpty(_.reject([ln1, ln2, city, locality, state, zipcode], isBlank));

  if (!preferredAddressAttributesExist && !isBlank(country)) {
    return country;
  }

  const cityPretty = getCityToDisplay(location);

  return _.reject([_.reject([ln1, ln2], isBlank).join(', '), cityPretty, [state, zipcode].join(' ')], isBlank).join(
    ', '
  );
};

export const formatAddressForCheckout = ({ ln1, ln2 }) => _.reject([ln1, ln2], isBlank).join(', ');

export const formatAddressShort = (location) => {
  const { ln1, ln2, state } = location;

  const cityPretty = getCityToDisplay(location);

  const displayFields = !ln1 ? [cityPretty, state] : [ln1, ln2];

  return _.reject(displayFields, isBlank).join(', ');
};

export const formatAddressWithoutApt = ({ ln1, city, state, zipcode, country }) => {
  const preferredAddressAttributesExist = !_.isEmpty(_.reject([ln1, city, state, zipcode], isBlank));

  if (!preferredAddressAttributesExist && !isBlank(country)) {
    return country;
  }

  return _.reject([_.reject([ln1], isBlank).join(', '), city, [state, zipcode].join(' ')], isBlank).join(', ');
};
